import * as v from "valibot";
import { usesCheckboxOptions } from "./checkbox-options.js";

import type { ValidationResult, FormEmbedField, FormEmbedFieldType } from "./types.js";

function stripToDigits(value: string): string {
  return value.replace(/\D/g, "");
}

export function formatPhoneInput(value: string): string {
  let digits = stripToDigits(value);
  if (digits.length === 0) return "";

  if (digits.length === 11 && digits[0] === "1") digits = digits.substring(1);
  digits = digits.substring(0, 10);

  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.substring(0, 3)}) ${digits.substring(3)}`;
  return `(${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6)}`;
}

export function validatePhoneNumber(value: string): ValidationResult {
  let digits = stripToDigits(value);

  if (digits.length === 11 && digits[0] === "1") digits = digits.substring(1);

  if (digits.length !== 10) return { isValid: false, formatted: null };

  // NANP: area code and exchange must start with 2-9
  if (digits[0] < "2" || digits[3] < "2") return { isValid: false, formatted: null };

  return { isValid: true, formatted: `+1${digits}` };
}

const MESSAGES = {
  email: "Please enter a valid email address",
  phone: "Please enter a valid phone number",
  number: "Please enter a valid number",
  minLength: (min: number) => `Must be at least ${min} characters`,
  maxLength: (max: number) => `Must be no more than ${max} characters`,
};

function formatFieldName(name: string): string {
  return name
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function getRequiredMessage(fieldName?: string, customMessage?: string): string {
  if (customMessage) return customMessage;
  if (fieldName) return `${formatFieldName(fieldName)} is required`;
  return "This field is required";
}

export function getFieldSchema(
  fieldType: FormEmbedFieldType,
  options?: {
    required?: boolean;
    minLength?: number;
    maxLength?: number;
    fieldName?: string;
    errorMessage?: string;
    options?: FormEmbedField["options"];
    multiSelect?: boolean;
  }
): v.GenericSchema {
  const { required = false, minLength, maxLength, fieldName, errorMessage } = options ?? {};
  const requiredMessage = getRequiredMessage(fieldName, errorMessage);

  if (usesCheckboxOptions({ type: fieldType, options: options?.options })) {
    const actions: v.PipeAction<string[], string[], v.BaseIssue<unknown>>[] = [];
    if (required) actions.push(v.minLength(1, requiredMessage));
    if (options?.multiSelect === false) actions.push(v.maxLength(1, "Select only one option"));
    return actions.length ? v.pipe(v.array(v.string()), ...actions) : v.array(v.string());
  }

  switch (fieldType) {
    case "email": {
      const base = required
        ? v.pipe(v.string(), v.nonEmpty(requiredMessage), v.email(MESSAGES.email))
        : v.pipe(v.string(), v.email(MESSAGES.email));
      return required ? base : v.optional(base);
    }

    case "phone": {
      const base = required
        ? v.pipe(
            v.string(),
            v.nonEmpty(requiredMessage),
            v.check((val: string) => !val || validatePhoneNumber(val).isValid, MESSAGES.phone),
          )
        : v.pipe(
            v.string(),
            v.check((val: string) => !val || validatePhoneNumber(val).isValid, MESSAGES.phone),
          );
      return base;
    }

    case "number":
      return v.pipe(
        v.union([v.string(), v.number()]),
        v.transform(Number),
        v.number(MESSAGES.number),
      );

    // The answer is whatever a pick puts there — street, city, state, ZIP and
    // country — so a length limit authored for a short text box would refuse a
    // valid suggestion. Required is the only rule.
    case "address": {
      return required
        ? v.pipe(v.string(), v.nonEmpty(requiredMessage))
        : v.optional(v.string());
    }

    case "textarea":
    case "text": {
      const actions: v.PipeAction<string, string, v.BaseIssue<unknown>>[] = [];
      if (required) actions.push(v.nonEmpty(requiredMessage));
      if (minLength) actions.push(v.minLength(minLength, MESSAGES.minLength(minLength)));
      if (maxLength) actions.push(v.maxLength(maxLength, MESSAGES.maxLength(maxLength)));
      const base = actions.length > 0
        ? v.pipe(v.string(), ...actions)
        : v.string();
      return required ? base : v.optional(base);
    }

    case "select":
    case "radio": {
      return required
        ? v.pipe(v.string(), v.nonEmpty(requiredMessage))
        : v.optional(v.string());
    }

    case "checkbox": {
      return required
        ? v.pipe(v.boolean(), v.check((val: boolean) => val === true, requiredMessage))
        : v.boolean();
    }

    default:
      return required
        ? v.pipe(v.string(), v.nonEmpty(requiredMessage))
        : v.optional(v.string());
  }
}

/**
 * @param isVisible For branching forms: whether a field shows right now. A
 *   hidden field is never validated — a required question on a branch the
 *   visitor didn't take must not block the submit. It must judge the raw form
 *   input, as rendering does: the values handed to the schema's checks are
 *   already transformed (a blank number reads as `0`), so they could route
 *   differently from what the visitor sees.
 */
export function buildFormSchema(
  fields: FormEmbedField[],
  consentRequired = false,
  isVisible?: (name: string) => boolean,
): v.GenericSchema {
  const entries: Record<string, v.GenericSchema> = {};
  const branchChecks: v.PipeAction<any, any, v.BaseIssue<unknown>>[] = [];

  for (const field of fields) {
    const schema = getFieldSchema(field.type, {
      required: field.required,
      minLength: field.minLength,
      maxLength: field.maxLength,
      fieldName: field.name,
      errorMessage: field.errorMessage,
      options: field.options,
      multiSelect: field.multiSelect,
    });

    if (!isVisible) {
      entries[field.name] = schema;
      continue;
    }

    // Visibility depends on the other answers, which a field schema can't see.
    // The field itself accepts anything (still transforming a valid value), and
    // an object-level check — reported on the field — applies the real rules
    // while it shows. `partialCheck` runs even when other fields are invalid,
    // so its error appears alongside theirs.
    entries[field.name] = v.union([schema, v.any()]);
    let message = "";
    branchChecks.push(
      v.forward(
        v.partialCheck(
          [[field.name]] as any,
          (input: any) => {
            if (!isVisible(field.name)) return true;
            const result = v.safeParse(schema, input[field.name]);
            if (result.success) return true;
            message = result.issues[0].message;
            return false;
          },
          () => message,
        ),
        [field.name] as any,
      ),
    );
  }

  if (consentRequired) {
    entries._consent = v.pipe(
      v.boolean(),
      v.check((val: boolean) => val === true, "You must agree to continue."),
    );
  } else {
    entries._consent = v.optional(v.boolean());
  }

  const object = v.object(entries);
  return branchChecks.length > 0 ? v.pipe(object, ...branchChecks) : object;
}
